//#region Imports

import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { IconComponent } from '../../../../../shared/components/icon/icon.component';
import { InputComponent } from '../../../../../shared/components/input/input.component';
import { CoverImageUploadComponent } from '../../../../../shared/components/cover-image-upload/cover-image-upload.component';
import { FileUploadDropzoneComponent } from '../../../../../shared/components/file-upload-dropzone/file-upload-dropzone.component';
import { FileUploadStatus } from '../../../../../shared/components/file-upload-dropzone/file-upload-status.type';
import { ToastService } from '../../../../../shared/components/toast/service/toast.service';
import { formatFileSizeMb } from '../../../../../shared/helpers/file-size.helper';
import { ClassForm } from '../../../../../shared/models/interfaces/forms/course-form.interface';

//#endregion

const MAX_VIDEO_BYTES = 200 * 1024 * 1024;
const MAX_THUMBNAIL_BYTES = 10 * 1024 * 1024;
const VIDEO_EXTENSIONS = ['.mp4', '.mov', '.avi'];
const THUMBNAIL_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.webp'];

@Component({
  selector: 'l-course-content-type-video',
  standalone: true,
  templateUrl: './course-content-type-video.component.html',
  imports: [ReactiveFormsModule, IconComponent, InputComponent, FileUploadDropzoneComponent, CoverImageUploadComponent],
})
export class CourseContentTypeVideoComponent {

  //#region Inputs

  @Input({ required: true })
  public form!: FormGroup<ClassForm>;

  @Input()
  public file: File | null = null;

  @Input()
  public fileSizeLabel: string = '';

  //#endregion

  //#region Outputs

  @Output()
  public readonly fileStateChange = new EventEmitter<{ file: File | null; fileSizeLabel: string }>();

  @Output()
  public readonly thumbnailFileChange = new EventEmitter<File | null>();

  //#endregion

  //#region Public Properties

  public readonly videoFileInputId: string = 'course-content-video-file';

  public readonly thumbnailExtensions = THUMBNAIL_EXTENSIONS;

  public readonly maxThumbnailBytes = MAX_THUMBNAIL_BYTES;

  public displayFileName: string = '';

  //#endregion

  //#region Private Properties

  private readonly toastService = inject(ToastService);

  //#endregion

  //#region Getters

  public get uploadStatus(): FileUploadStatus {
    return this.displayFileName || this.form.controls.videoFileName.value ? 'uploaded' : 'idle';
  }

  public get uploadedTitle(): string {
    return isHttpUrl(this.form.controls.videoFileName.value) ? 'Arquivo enviado!' : 'Arquivo selecionado';
  }

  public get shownFileName(): string {
    return this.displayFileName || storedFileLabel(this.form.controls.videoFileName.value);
  }

  //#endregion

  //#region Public Methods

  public onVideoFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const selectedFile = input?.files?.[0] ?? null;

    if (!selectedFile) {
      this.displayFileName = '';
      this.fileStateChange.emit({ file: null, fileSizeLabel: '' });
      return;
    }

    if (!hasExtension(selectedFile.name, VIDEO_EXTENSIONS)) {
      this.toastService.error('Formatos aceitos: MP4, MOV e AVI.');
      this.clearSelection(input);
      return;
    }

    if (selectedFile.size > MAX_VIDEO_BYTES) {
      this.toastService.error('Arquivo muito grande. Tamanho máximo: 200MB');
      this.clearSelection(input);
      return;
    }

    this.displayFileName = selectedFile.name;
    this.fileStateChange.emit({
      file: selectedFile,
      fileSizeLabel: formatFileSizeMb(selectedFile.size),
    });
  }

  public onThumbnailUrlChange(url: string): void {
    this.form.controls.videoThumbnailUrl.setValue(url);
  }

  public onThumbnailFileChange(file: File | null): void {
    this.thumbnailFileChange.emit(file);
  }

  //#endregion

  //#region Private Methods

  private clearSelection(input: HTMLInputElement | null): void {
    if (input) {
      input.value = '';
    }

    this.displayFileName = '';
    this.fileStateChange.emit({ file: null, fileSizeLabel: '' });
  }

  //#endregion

}

function storedFileLabel(value: string): string {
  const trimmed = value.trim();

  if (!trimmed) {
    return '';
  }

  try {
    const segment = new URL(trimmed).pathname.split('/').findLast((part) => part.length > 0) ?? trimmed;
    return decodeURIComponent(segment);
  } catch {
    return trimmed;
  }
}

function isHttpUrl(value: string): boolean {
  return /^https?:\/\//i.test(value.trim());
}

function hasExtension(fileName: string, extensions: string[]): boolean {
  const lowerName = fileName.toLowerCase();
  return extensions.some((extension) => lowerName.endsWith(extension));
}
